import styled from '@emotion/styled';
import { Box, Link, Typography, TypographyProps } from '@mui/material';
import { useRef, useState } from 'react';
import { WsColors } from 'wisestep-base';

import useTypographyOverFlowCheck from '../../../../../features/candidateLayout/hooks/useTypographyOverFlowCheck';

const collapsedTypographyStyledProps = {
  color: WsColors.GenericRichBlack,
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
  display: 'inline-block',
};

const expandedTypographyStyledProps = {
  color: WsColors.GenericRichBlack,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  display: 'inline',
  fontWeight: 500,
};

interface ExpandableTypographyProps extends TypographyProps {
  content: string;
  maxWidth?: number | string;
}

export const StyledLink = styled(Link)(() => ({
  flexShrink: 0,
  cursor: 'pointer',
}));
const ExpandableTypography: React.FC<ExpandableTypographyProps> = ({
  content,
  maxWidth = '100%',
  ...typographyProps
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isOverflowing } = useTypographyOverFlowCheck(content, textRef, containerRef);
  const [expanded, setExpanded] = useState(false);

  const toggleExpand = () => setExpanded((prev) => !prev);

  return (
    <Box ref={containerRef} sx={{ display: 'inline-flex', alignItems: 'center', maxWidth }}>
      {!expanded ? (
        <>
          <Typography
            component="span"
            ref={textRef}
            noWrap
            {...typographyProps}
            sx={collapsedTypographyStyledProps}
          >
            {content}
          </Typography>

          {isOverflowing && (
            <StyledLink underline="none" variant="body1" onClick={toggleExpand}>
              more
            </StyledLink>
          )}
        </>
      ) : (
        <>
          <Typography component="span" {...typographyProps} sx={expandedTypographyStyledProps}>
            {content}
            <StyledLink
              underline="none"
              variant="body1"
              onClick={toggleExpand}
            >{` less`}</StyledLink>
          </Typography>
        </>
      )}
    </Box>
  );
};

export default ExpandableTypography;
